import React from 'react';
import { Box, Typography, IconButton, Dialog, Slider, Autocomplete, TextField, Select, MenuItem, FormControl, Chip, Button } from '@mui/material';
import { Clear } from '@mui/icons-material';

const SearchFiltersDialog = ({
    open,
    onClose,
    experienceMin, setExperienceMin,
    experienceMax, setExperienceMax,
    seniorityLevel, setSeniorityLevel, availableSeniorities = ['Tous'],
    selectedSkills, setSelectedSkills, availableSkills = [],
    selectedLanguages, setSelectedLanguages, availableLanguages = [],
    selectedEducation, setSelectedEducation, availableEducation = [],
    selectedLocation, setSelectedLocation,
    minCandidateExp = 0, maxCandidateExp = 20,
}) => {
    
    const [localExpMin, setLocalExpMin] = React.useState(experienceMin);
    const [localExpMax, setLocalExpMax] = React.useState(experienceMax);
    const [localSeniority, setLocalSeniority] = React.useState(seniorityLevel);
    const [localSkills, setLocalSkills] = React.useState(selectedSkills);
    const [localLanguages, setLocalLanguages] = React.useState(selectedLanguages);
    const [localEducation, setLocalEducation] = React.useState(selectedEducation);
    const [localLocation, setLocalLocation] = React.useState(selectedLocation);

    React.useEffect(() => {
        if (open) {
            setLocalExpMin(Math.max(minCandidateExp, Math.min(experienceMin, maxCandidateExp)));
            setLocalExpMax(Math.max(minCandidateExp, Math.min(experienceMax, maxCandidateExp)));
            setLocalSeniority(seniorityLevel);
            setLocalSkills(selectedSkills);
            setLocalLanguages(selectedLanguages);
            setLocalEducation(selectedEducation);
            setLocalLocation(selectedLocation);
        }
    }, [open, experienceMin, experienceMax, seniorityLevel, selectedSkills, selectedLanguages, selectedEducation, selectedLocation, minCandidateExp, maxCandidateExp]);

    const handleApply = () => {
        setExperienceMin(localExpMin);
        setExperienceMax(localExpMax);
        setSeniorityLevel(localSeniority);
        setSelectedSkills(localSkills);
        setSelectedLanguages(localLanguages);
        setSelectedEducation(localEducation);
        setSelectedLocation(localLocation);
        onClose();
    };

    const handleReset = () => {
        setLocalExpMin(minCandidateExp);
        setLocalExpMax(maxCandidateExp);
        setLocalSeniority('Tous');
        setLocalSkills([]);
        setLocalLanguages([]);
        setLocalEducation('Tous');
        setLocalLocation('');
    };

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="sm"
            slotProps={{ paper: {
                elevation: 0,
                sx: {
                    width: { xs: '90vw', sm: 400 }, borderRadius: 4, border: '1px solid #e2e8f0',
                    overflow: 'hidden',
                    boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
                }
            } }}
        >
            <Box sx={{ px: 3, py: 2, borderBottom: '1px solid #f1f5f9', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography sx={{ fontWeight: 700, fontSize: '0.85rem', color: '#0f172a', letterSpacing: '0.5px' }}>
                    Filtres Frontend (Expérience & Séniorité)
                </Typography>
                <IconButton aria-label="Fermer les filtres" size="small" onClick={onClose} sx={{ p: 0.5, color: '#94a3b8', '&:hover': { color: '#0f172a', backgroundColor: '#f1f5f9' } }}>
                    <Clear sx={{ fontSize: '1.1rem' }} />
                </IconButton>
            </Box>

            <Box sx={{ p: 3, display: 'flex', flexDirection: 'column', gap: 3.5, maxHeight: '70vh', overflowY: 'auto' }}>
                <Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                        <Typography sx={{ fontWeight: 600, fontSize: '0.7rem', color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                            Expérience requise
                        </Typography>
                        <Typography sx={{ fontWeight: 700, fontSize: '0.75rem', color: '#0f172a', backgroundColor: '#f1f5f9', px: 1, py: 0.3, borderRadius: 1 }}>
                            {localExpMin} - {localExpMax} ans
                        </Typography>
                    </Box>
                    <Box sx={{ px: 1, mt: 1.5 }}>
                        <Slider
                            value={[localExpMin, localExpMax]}
                            min={minCandidateExp} max={Math.max(minCandidateExp + 1, maxCandidateExp)}
                            onChange={(_, v) => { setLocalExpMin(v[0]); setLocalExpMax(v[1]); }}
                            size="small"
                            sx={{
                                color: '#0f172a',
                                height: 4,
                                '& .MuiSlider-thumb': {
                                    width: 18, height: 18, backgroundColor: '#fff',
                                    border: '2px solid #0f172a',
                                    boxShadow: '0 2px 4px rgba(0,0,0,0.15)',
                                    '&:hover': { boxShadow: '0 0 0 8px rgba(15, 23, 42, 0.1)' }
                                },
                                '& .MuiSlider-rail': { backgroundColor: '#e2e8f0', opacity: 1 },
                                '& .MuiSlider-track': { border: 'none' }
                            }}
                        />
                    </Box>
                </Box>

                <Box>
                    <Typography sx={{ fontWeight: 600, fontSize: '0.7rem', color: '#64748b', mb: 1.5, textTransform: 'uppercase', letterSpacing: '0.5px', display: 'block' }}>
                        Niveau de séniorité
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                        {availableSeniorities.map((level) => (
                            <Box
                                key={level}
                                onClick={() => setLocalSeniority(level)}
                                sx={{
                                    px: 1.8, py: 0.6, borderRadius: '20px', cursor: 'pointer', fontSize: '0.75rem',
                                    backgroundColor: localSeniority === level ? '#0f172a' : '#fff',
                                    color: localSeniority === level ? '#fff' : '#475569',
                                    fontWeight: localSeniority === level ? 600 : 500,
                                    border: '1px solid',
                                    borderColor: localSeniority === level ? '#0f172a' : '#cbd5e1',
                                    transition: 'all 0.2s ease',
                                    boxShadow: localSeniority === level ? '0 4px 6px -1px rgba(15, 23, 42, 0.2)' : 'none',
                                    '&:hover': {
                                        backgroundColor: localSeniority === level ? '#1e293b' : '#f8fafc',
                                        borderColor: localSeniority === level ? '#1e293b' : '#94a3b8'
                                    }
                                }}
                            >
                                {level}
                            </Box>
                        ))}
                    </Box>
                </Box>

                <Box>
                    <Typography sx={{ fontWeight: 600, fontSize: '0.7rem', color: '#64748b', mb: 1.5, textTransform: 'uppercase', letterSpacing: '0.5px', display: 'block' }}>
                        Compétences Techniques
                    </Typography>
                    <Autocomplete
                        multiple
                        size="small"
                        options={availableSkills}
                        value={localSkills}
                        onChange={(_, newValue) => setLocalSkills(newValue)}
                        renderTags={(value, getTagProps) =>
                            value.map((option, index) => (
                                <Chip variant="outlined" label={option} size="small" {...getTagProps({ index })} />
                            ))
                        }
                        renderInput={(params) => (
                            <TextField {...params} placeholder="Ajouter une compétence" variant="outlined" />
                        )}
                    />
                </Box>

                <Box>
                    <Typography sx={{ fontWeight: 600, fontSize: '0.7rem', color: '#64748b', mb: 1.5, textTransform: 'uppercase', letterSpacing: '0.5px', display: 'block' }}>
                        Langues Parlées
                    </Typography>
                    <Autocomplete
                        multiple
                        size="small"
                        options={availableLanguages}
                        value={localLanguages}
                        onChange={(_, newValue) => setLocalLanguages(newValue)}
                        renderTags={(value, getTagProps) =>
                            value.map((option, index) => (
                                <Chip variant="outlined" label={option} size="small" {...getTagProps({ index })} />
                            ))
                        }
                        renderInput={(params) => (
                            <TextField {...params} placeholder="Ajouter une langue" variant="outlined" />
                        )}
                    />
                </Box>

                <Box sx={{ display: 'flex', gap: 2, flexDirection: { xs: 'column', sm: 'row' } }}>
                    <Box sx={{ flex: 1 }}>
                        <Typography sx={{ fontWeight: 600, fontSize: '0.7rem', color: '#64748b', mb: 1.5, textTransform: 'uppercase', letterSpacing: '0.5px', display: 'block' }}>
                            Niveau d'étude
                        </Typography>
                        <FormControl fullWidth size="small">
                            <Select
                                value={localEducation}
                                onChange={(e) => setLocalEducation(e.target.value)}
                                displayEmpty
                            >
                                <MenuItem value="Tous"><em>Tous</em></MenuItem>
                                {availableEducation.filter(e => e !== 'Tous').map(edu => (
                                    <MenuItem key={edu} value={edu}>{edu}</MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Box>
                    <Box sx={{ flex: 1 }}>
                        <Typography sx={{ fontWeight: 600, fontSize: '0.7rem', color: '#64748b', mb: 1.5, textTransform: 'uppercase', letterSpacing: '0.5px', display: 'block' }}>
                            Localisation
                        </Typography>
                        <TextField
                            size="small"
                            fullWidth
                            placeholder="Ex: Casablanca"
                            value={localLocation}
                            onChange={(e) => setLocalLocation(e.target.value)}
                            variant="outlined"
                        />
                    </Box>
                </Box>
            </Box>

            <Box sx={{ px: 3, py: 1.5, borderTop: '1px solid #f1f5f9', backgroundColor: '#f8fafc', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography
                    onClick={handleReset}
                    sx={{
                        fontSize: '0.75rem', fontWeight: 600, color: '#64748b', cursor: 'pointer',
                        transition: 'color 0.2s', '&:hover': { color: '#0f172a' }
                    }}
                >
                    Réinitialiser
                </Typography>
                <Button
                    variant="contained"
                    onClick={handleApply}
                    sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 'bold', backgroundColor: '#1976d2', '&:hover': { backgroundColor: '#1565c0' }, px: 3 }}
                >
                    Appliquer les filtres
                </Button>
            </Box>
        </Dialog>
    );
};

export default SearchFiltersDialog;

